<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { useMapGetter } from 'dashboard/composables/store';
import {
  formatCurrencyFromCents,
  resolveCrmCurrency,
} from '../../../../../crm/utils/currency';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const { t, locale } = useI18n();
const route = useRoute();
const getAccountById = useMapGetter('accounts/getAccount');

const isLoading = ref(false);
const deals = ref([]);
const errorMessage = ref('');
const pollIntervalId = ref(null);

const accountId = computed(() => {
  const parsed = Number.parseInt(route.params.accountId, 10);
  return Number.isNaN(parsed) ? null : parsed;
});

const currentAccount = computed(() => {
  if (!accountId.value) {
    return null;
  }

  return getAccountById.value(accountId.value) || null;
});

const crmCurrency = computed(() => resolveCrmCurrency(currentAccount.value));

const crmPath = computed(() => {
  if (!accountId.value) {
    return null;
  }

  return `/app/accounts/${accountId.value}/crm?pipeline=support`;
});

const formattedLastSignalAt = rawValue => {
  if (!rawValue) {
    return t('CONVERSATION_SIDEBAR.CRM_DEAL.NO_SIGNAL');
  }

  const parsedDate = new Date(rawValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return t('CONVERSATION_SIDEBAR.CRM_DEAL.NO_SIGNAL');
  }

  return new Intl.DateTimeFormat((locale.value || 'en').replace('_', '-'), {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(parsedDate);
};

const emotionLabel = emotionValue => {
  const normalizedEmotion = `${emotionValue || ''}`.trim();
  if (!normalizedEmotion) {
    return t('CRM.MODAL.FORM.EMOTION_OPTIONS.UNKNOWN');
  }

  return t(`CRM.MODAL.FORM.EMOTION_OPTIONS.${normalizedEmotion.toUpperCase()}`);
};

const priorityLabel = priorityValue => {
  const normalizedPriority = `${priorityValue || ''}`.trim();
  if (!normalizedPriority) {
    return null;
  }

  return t(
    `CRM.MODAL.FORM.PRIORITY_OPTIONS.${normalizedPriority.toUpperCase()}`
  );
};

const fetchDeals = async () => {
  if (!accountId.value) {
    deals.value = [];
    return;
  }

  const normalizedConversationId = Number.parseInt(props.conversationId, 10);
  if (Number.isNaN(normalizedConversationId)) {
    deals.value = [];
    return;
  }

  isLoading.value = true;
  errorMessage.value = '';

  try {
    const response = await window.axios.get(
      `/api/v1/accounts/${accountId.value}/crm/service_orders`,
      {
        params: {
          pipeline: 'support',
          conversation_id: normalizedConversationId,
        },
      }
    );
    deals.value = response.data.payload || [];
  } catch (error) {
    errorMessage.value =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      t('CONVERSATION_SIDEBAR.CRM_DEAL.ERROR');
    deals.value = [];
  } finally {
    isLoading.value = false;
  }
};

const refreshDeals = () => {
  fetchDeals();
};

const startPolling = () => {
  if (pollIntervalId.value) {
    window.clearInterval(pollIntervalId.value);
  }

  if (!accountId.value) {
    return;
  }

  pollIntervalId.value = window.setInterval(() => {
    fetchDeals();
  }, 10_000);
};

onMounted(() => {
  fetchDeals();
  startPolling();
});

onBeforeUnmount(() => {
  if (pollIntervalId.value) {
    window.clearInterval(pollIntervalId.value);
  }
});

watch(
  () => props.conversationId,
  () => {
    fetchDeals();
  }
);

watch(accountId, newAccountId => {
  if (!newAccountId) {
    deals.value = [];
    return;
  }

  fetchDeals();
  startPolling();
});
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex items-center justify-between gap-2">
      <p class="text-xs text-n-slate-11">
        {{ $t('CONVERSATION_SIDEBAR.CRM_DEAL.SUBTITLE') }}
      </p>
      <div class="flex items-center gap-1">
        <NextButton
          size="xs"
          color="slate"
          variant="ghost"
          icon="i-lucide-refresh-cw"
          :title="$t('CONVERSATION_SIDEBAR.CRM_DEAL.REFRESH')"
          @click="refreshDeals"
        />
        <a
          v-if="crmPath"
          :href="crmPath"
          class="inline-flex items-center rounded-md border border-n-weak px-2 py-1 text-xs font-medium text-n-slate-11 hover:bg-n-alpha-1"
        >
          {{ $t('CONVERSATION_SIDEBAR.CRM_DEAL.OPEN_CRM') }}
        </a>
      </div>
    </div>

    <div v-if="isLoading" class="text-xs text-n-slate-10">
      {{ $t('CONVERSATION_SIDEBAR.CRM_DEAL.LOADING') }}
    </div>

    <div v-else-if="errorMessage" class="text-xs text-n-ruby-11">
      {{ errorMessage }}
    </div>

    <div
      v-else-if="deals.length === 0"
      class="rounded-lg border border-dashed border-n-weak bg-n-alpha-1 px-3 py-2 text-xs text-n-slate-10"
    >
      {{ $t('CONVERSATION_SIDEBAR.CRM_DEAL.EMPTY') }}
    </div>

    <div v-else class="flex flex-col gap-2">
      <div
        v-for="deal in deals"
        :key="deal.id"
        class="rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2"
      >
        <div class="flex items-start justify-between gap-2">
          <p class="text-sm font-medium text-n-slate-12">
            {{ deal.title }}
          </p>
          <span class="text-[11px] text-n-slate-10">#{{ deal.id }}</span>
        </div>

        <div class="mt-2 flex flex-wrap gap-1.5">
          <span
            class="rounded-full bg-n-amber-3 px-2 py-0.5 text-[11px] font-medium text-n-amber-11"
          >
            {{ emotionLabel(deal.crm_data?.emotion_label) }}
          </span>
          <span
            v-if="deal.crm_data?.satisfaction_score"
            class="rounded-full bg-n-emerald-3 px-2 py-0.5 text-[11px] font-medium text-n-emerald-11"
          >
            {{
              $t('CONVERSATION_SIDEBAR.CRM_DEAL.SATISFACTION', {
                score: deal.crm_data.satisfaction_score,
              })
            }}
          </span>
          <span
            v-if="deal.crm_data?.priority_level"
            class="rounded-full bg-n-ruby-3 px-2 py-0.5 text-[11px] font-medium text-n-ruby-11"
          >
            {{ priorityLabel(deal.crm_data.priority_level) }}
          </span>
          <span
            v-if="deal.crm_data?.value_cents"
            class="rounded-full bg-n-blue-3 px-2 py-0.5 text-[11px] font-medium text-n-blue-11"
          >
            {{
              formatCurrencyFromCents({
                valueCents: deal.crm_data.value_cents,
                currency: crmCurrency,
                locale: locale.value,
              })
            }}
          </span>
        </div>

        <p class="mt-2 text-[11px] text-n-slate-10">
          {{
            $t('CONVERSATION_SIDEBAR.CRM_DEAL.LAST_SIGNAL_AT', {
              value: formattedLastSignalAt(deal.crm_data?.last_signal_at),
            })
          }}
        </p>
      </div>
    </div>
  </div>
</template>
